import { useState } from 'react';
import { useAtlasStore } from '@/stores/atlasStore';
import { Home, Eye, EyeOff, Map, Satellite, Mountain, Sun, Palette, TrendingUp, ChevronDown, ChevronRight, Triangle, Compass, Shapes, Wind, Droplets, GitBranch, Waves, Hexagon, AlertTriangle, Target, Route, Building2, Trees, MapPin, Landmark, Shield, ScrollText, Wheat, Loader2 } from 'lucide-react';

function LayerToggle({ visible, onToggle, icon: Icon, label, color, opacity, onOpacity, accentClass, loading }: {
  visible: boolean; onToggle: () => void; icon: any; label: string;
  color: string; opacity: number; onOpacity: (v: number) => void; accentClass: string; loading?: boolean;
}) {
  return (
    <>
      <button
        onClick={onToggle}
        className={`flex items-center gap-2 px-2 py-1 rounded-md transition-colors text-xs font-medium ${
          visible ? color : 'text-clay hover:text-limestone hover:bg-basalt'
        }`}
        title={visible ? `Ocultar ${label}` : `Mostrar ${label}`}
      >
        {loading ? <Loader2 size={14} className="animate-spin text-terracotta" /> : <Icon size={14} />}
        <span>{label}</span>
      </button>
      {visible && (
        <div className="flex items-center gap-2 px-2 pb-1">
          <input type="range" min="0" max="100" step="5" value={Math.round(opacity * 100)}
            onChange={(e) => onOpacity(parseInt(e.target.value) / 100)}
            className={`flex-1 h-1 ${accentClass}`} />
          <span className="text-[10px] text-clay w-7 text-right">{Math.round(opacity * 100)}%</span>
        </div>
      )}
    </>
  );
}

export function Toolbar() {
  const showPolygon = useAtlasStore((s) => s.showPolygon);
  const setShowPolygon = useAtlasStore((s) => s.setShowPolygon);
  const baseLayer = useAtlasStore((s) => s.baseLayer);
  const setBaseLayer = useAtlasStore((s) => s.setBaseLayer);

  // Lazy-load wiring (EB-56): raster rows show by availability and fetch on first toggle.
  const loadLayer = useAtlasStore((s) => s.loadLayer);
  const layerLoading = useAtlasStore((s) => s.layerLoading);
  const rasterAvailable = useAtlasStore((s) => s.rasterAvailable);
  const setLayerLoading = useAtlasStore((s) => s.setLayerLoading);
  const toggleRaster = (key: string, visible: boolean, setVisible: (v: boolean) => void, tileUrl: string | null) => {
    const next = !visible;
    setVisible(next);
    if (next && !tileUrl) { setLayerLoading(key, true); loadLayer?.(key); }
  };

  // Terreno Base — now backed by standard products (LOLA/DGT provider)
  const mdtVisible = useAtlasStore((s) => s.mdtVisible);
  const setMdtVisible = useAtlasStore((s) => s.setMdtVisible);
  const mdtTileUrl = useAtlasStore((s) => s.mdtTileUrl);
  const mdtOpacity = useAtlasStore((s) => s.mdtOpacity);
  const setMdtOpacity = useAtlasStore((s) => s.setMdtOpacity);

  const hillshadeVisible = useAtlasStore((s) => s.hillshadeVisible);
  const setHillshadeVisible = useAtlasStore((s) => s.setHillshadeVisible);
  const hillshadeTileUrl = useAtlasStore((s) => s.hillshadeTileUrl);
  const hillshadeOpacity = useAtlasStore((s) => s.hillshadeOpacity);
  const setHillshadeOpacity = useAtlasStore((s) => s.setHillshadeOpacity);

  const hillshadeHypsoVisible = useAtlasStore((s) => s.hillshadeHypsoVisible);
  const setHillshadeHypsoVisible = useAtlasStore((s) => s.setHillshadeHypsoVisible);
  const hillshadeHypsoTileUrl = useAtlasStore((s) => s.hillshadeHypsoTileUrl);
  const hillshadeHypsoOpacity = useAtlasStore((s) => s.hillshadeHypsoOpacity);
  const setHillshadeHypsoOpacity = useAtlasStore((s) => s.setHillshadeHypsoOpacity);

  const contoursVisible = useAtlasStore((s) => s.contoursVisible);
  const setContoursVisible = useAtlasStore((s) => s.setContoursVisible);
  const contoursTileUrl = useAtlasStore((s) => s.contoursTileUrl);
  const contoursOpacity = useAtlasStore((s) => s.contoursOpacity);
  const setContoursOpacity = useAtlasStore((s) => s.setContoursOpacity);

  // Slope (Declives) — Morfologia group
  const slopeVisible = useAtlasStore((s) => s.slopeVisible);
  const setSlopeVisible = useAtlasStore((s) => s.setSlopeVisible);
  const slopeTileUrl = useAtlasStore((s) => s.slopeTileUrl);
  const slopeOpacity = useAtlasStore((s) => s.slopeOpacity);
  const setSlopeOpacity = useAtlasStore((s) => s.setSlopeOpacity);
  // Aspect (Exposição Solar) — Morfologia group
  const aspectVisible = useAtlasStore((s) => s.aspectVisible);
  const setAspectVisible = useAtlasStore((s) => s.setAspectVisible);
  const aspectTileUrl = useAtlasStore((s) => s.aspectTileUrl);
  const aspectOpacity = useAtlasStore((s) => s.aspectOpacity);
  const setAspectOpacity = useAtlasStore((s) => s.setAspectOpacity);
  // Geomorphons (10 Landforms) — Morfologia group
  const geomorphonsVisible = useAtlasStore((s) => s.geomorphonsVisible);
  const setGeomorphonsVisible = useAtlasStore((s) => s.setGeomorphonsVisible);
  const geomorphonsTileUrl = useAtlasStore((s) => s.geomorphonsTileUrl);
  const geomorphonsOpacity = useAtlasStore((s) => s.geomorphonsOpacity);
  const setGeomorphonsOpacity = useAtlasStore((s) => s.setGeomorphonsOpacity);
  // Wind Exposition (Exposição ao Vento) — Morfologia group
  const windExpositionVisible = useAtlasStore((s) => s.windExpositionVisible);
  const setWindExpositionVisible = useAtlasStore((s) => s.setWindExpositionVisible);
  const windExpositionTileUrl = useAtlasStore((s) => s.windExpositionTileUrl);
  const windExpositionOpacity = useAtlasStore((s) => s.windExpositionOpacity);
  const setWindExpositionOpacity = useAtlasStore((s) => s.setWindExpositionOpacity);

  // Streams (Linhas de Água) — Hidrologia group
  const streamsVisible = useAtlasStore((s) => s.streamsVisible);
  const setStreamsVisible = useAtlasStore((s) => s.setStreamsVisible);
  const streamsTileUrl = useAtlasStore((s) => s.streamsTileUrl);
  const streamsOpacity = useAtlasStore((s) => s.streamsOpacity);
  const setStreamsOpacity = useAtlasStore((s) => s.setStreamsOpacity);

  // Basins (Bacias) — Hidrologia group
  const basinsVisible = useAtlasStore((s) => s.basinsVisible);
  const setBasinsVisible = useAtlasStore((s) => s.setBasinsVisible);
  const basinsTileUrl = useAtlasStore((s) => s.basinsTileUrl);
  const basinsOpacity = useAtlasStore((s) => s.basinsOpacity);
  const setBasinsOpacity = useAtlasStore((s) => s.setBasinsOpacity);
  // Flow Accumulation (Acumulação) — Hidrologia group
  const flowAccVisible = useAtlasStore((s) => s.flowAccVisible);
  const setFlowAccVisible = useAtlasStore((s) => s.setFlowAccVisible);
  const flowAccTileUrl = useAtlasStore((s) => s.flowAccTileUrl);
  const flowAccOpacity = useAtlasStore((s) => s.flowAccOpacity);
  const setFlowAccOpacity = useAtlasStore((s) => s.setFlowAccOpacity);
  // Ridges (Festos) — Hidrologia group
  const ridgesVisible = useAtlasStore((s) => s.ridgesVisible);
  const setRidgesVisible = useAtlasStore((s) => s.setRidgesVisible);
  const ridgesTileUrl = useAtlasStore((s) => s.ridgesTileUrl);
  const ridgesOpacity = useAtlasStore((s) => s.ridgesOpacity);
  const setRidgesOpacity = useAtlasStore((s) => s.setRidgesOpacity);
  // Keypoints (Inflexão de Vale) — Landforms & Keyline group
  const keypointsVisible = useAtlasStore((s) => s.keypointsVisible);
  const setKeypointsVisible = useAtlasStore((s) => s.setKeypointsVisible);
  const keypointsTileUrl = useAtlasStore((s) => s.keypointsTileUrl);
  const keypointsOpacity = useAtlasStore((s) => s.keypointsOpacity);
  const setKeypointsOpacity = useAtlasStore((s) => s.setKeypointsOpacity);
  // Primary Valleys (Vales Primários) — Landforms & Keyline group
  const primaryValleysVisible = useAtlasStore((s) => s.primaryValleysVisible);
  const setPrimaryValleysVisible = useAtlasStore((s) => s.setPrimaryValleysVisible);
  const primaryValleysTileUrl = useAtlasStore((s) => s.primaryValleysTileUrl);
  const primaryValleysOpacity = useAtlasStore((s) => s.primaryValleysOpacity);
  const setPrimaryValleysOpacity = useAtlasStore((s) => s.setPrimaryValleysOpacity);
  // Primary Ridges (Cumeadas Primárias) — Landforms & Keyline group
  const primaryRidgesVisible = useAtlasStore((s) => s.primaryRidgesVisible);
  const setPrimaryRidgesVisible = useAtlasStore((s) => s.setPrimaryRidgesVisible);
  const primaryRidgesTileUrl = useAtlasStore((s) => s.primaryRidgesTileUrl);
  const primaryRidgesOpacity = useAtlasStore((s) => s.primaryRidgesOpacity);
  const setPrimaryRidgesOpacity = useAtlasStore((s) => s.setPrimaryRidgesOpacity);
  // Erosion Risk (Risco de Erosão) — Erosão group
  const erosionRiskVisible = useAtlasStore((s) => s.erosionRiskVisible);
  const setErosionRiskVisible = useAtlasStore((s) => s.setErosionRiskVisible);
  const erosionRiskTileUrl = useAtlasStore((s) => s.erosionRiskTileUrl);
  const erosionRiskOpacity = useAtlasStore((s) => s.erosionRiskOpacity);
  const setErosionRiskOpacity = useAtlasStore((s) => s.setErosionRiskOpacity);
  // Flood Zones (Áreas Inundáveis) — Hidrologia group
  const floodZonesVisible = useAtlasStore((s) => s.floodZonesVisible);
  const setFloodZonesVisible = useAtlasStore((s) => s.setFloodZonesVisible);
  const floodZonesTileUrl = useAtlasStore((s) => s.floodZonesTileUrl);
  const floodZonesOpacity = useAtlasStore((s) => s.floodZonesOpacity);
  const setFloodZonesOpacity = useAtlasStore((s) => s.setFloodZonesOpacity);
  // OSM Context — Roads & Buildings
  const roadsVisible = useAtlasStore((s) => s.roadsVisible);
  const setRoadsVisible = useAtlasStore((s) => s.setRoadsVisible);
  const roadsOpacity = useAtlasStore((s) => s.roadsOpacity);
  const setRoadsOpacity = useAtlasStore((s) => s.setRoadsOpacity);
  const buildingsVisible = useAtlasStore((s) => s.buildingsVisible);
  const setBuildingsVisible = useAtlasStore((s) => s.setBuildingsVisible);
  const buildingsOpacity = useAtlasStore((s) => s.buildingsOpacity);
  const setBuildingsOpacity = useAtlasStore((s) => s.setBuildingsOpacity);
  const landuseVisible = useAtlasStore((s) => s.landuseVisible);
  const setLanduseVisible = useAtlasStore((s) => s.setLanduseVisible);
  const landuseOpacity = useAtlasStore((s) => s.landuseOpacity);
  const setLanduseOpacity = useAtlasStore((s) => s.setLanduseOpacity);
  const waterwaysVisible = useAtlasStore((s) => s.waterwaysVisible);
  const setWaterwaysVisible = useAtlasStore((s) => s.setWaterwaysVisible);
  const waterwaysOpacity = useAtlasStore((s) => s.waterwaysOpacity);
  const setWaterwaysOpacity = useAtlasStore((s) => s.setWaterwaysOpacity);
  const adminBoundsVisible = useAtlasStore((s) => s.adminBoundsVisible);
  const setAdminBoundsVisible = useAtlasStore((s) => s.setAdminBoundsVisible);
  const adminBoundsOpacity = useAtlasStore((s) => s.adminBoundsOpacity);
  const setAdminBoundsOpacity = useAtlasStore((s) => s.setAdminBoundsOpacity);

  // Institutional (PR5c) — Cadastro + Restricoes Legais groups
  const cadastroDgtVisible = useAtlasStore((s) => s.cadastroDgtVisible);
  const setCadastroDgtVisible = useAtlasStore((s) => s.setCadastroDgtVisible);
  const cadastroDgtOpacity = useAtlasStore((s) => s.cadastroDgtOpacity);
  const setCadastroDgtOpacity = useAtlasStore((s) => s.setCadastroDgtOpacity);
  const bupiRggVisible = useAtlasStore((s) => s.bupiRggVisible);
  const setBupiRggVisible = useAtlasStore((s) => s.setBupiRggVisible);
  const bupiRggOpacity = useAtlasStore((s) => s.bupiRggOpacity);
  const setBupiRggOpacity = useAtlasStore((s) => s.setBupiRggOpacity);
  const freguesiasVisible = useAtlasStore((s) => s.freguesiasVisible);
  const setFreguesiasVisible = useAtlasStore((s) => s.setFreguesiasVisible);
  const freguesiasOpacity = useAtlasStore((s) => s.freguesiasOpacity);
  const setFreguesiasOpacity = useAtlasStore((s) => s.setFreguesiasOpacity);
  const concelhosVisible = useAtlasStore((s) => s.concelhosVisible);
  const setConcelhosVisible = useAtlasStore((s) => s.setConcelhosVisible);
  const concelhosOpacity = useAtlasStore((s) => s.concelhosOpacity);
  const setConcelhosOpacity = useAtlasStore((s) => s.setConcelhosOpacity);
  const renVisible = useAtlasStore((s) => s.renVisible);
  const setRenVisible = useAtlasStore((s) => s.setRenVisible);
  const renOpacity = useAtlasStore((s) => s.renOpacity);
  const setRenOpacity = useAtlasStore((s) => s.setRenOpacity);
  const renExclusoesVisible = useAtlasStore((s) => s.renExclusoesVisible);
  const setRenExclusoesVisible = useAtlasStore((s) => s.setRenExclusoesVisible);
  const renExclusoesOpacity = useAtlasStore((s) => s.renExclusoesOpacity);
  const setRenExclusoesOpacity = useAtlasStore((s) => s.setRenExclusoesOpacity);
  const ranVisible = useAtlasStore((s) => s.ranVisible);
  const setRanVisible = useAtlasStore((s) => s.setRanVisible);
  const ranOpacity = useAtlasStore((s) => s.ranOpacity);
  const setRanOpacity = useAtlasStore((s) => s.setRanOpacity);

  // With no order selected (`?order=` absent) the map is empty, so the toolbar
  // shows zero layers. Order switching is a full navigation, so this is stable
  // per page load. Gates the three otherwise-always-on groups below.
  const orderSelected = new URLSearchParams(window.location.search).get('order') != null;

  const [terrenoOpen, setTerrenoOpen] = useState(true);
  const [morfologiaOpen, setMorfologiaOpen] = useState(true);
  const [landformsOpen, setLandformsOpen] = useState(true);
  const [erosaoOpen, setErosaoOpen] = useState(true);
  const [hidrologiaOpen, setHidrologiaOpen] = useState(true);
  const [cadastroOpen, setCadastroOpen] = useState(true);
  const [restricoesOpen, setRestricoesOpen] = useState(false);
  const [contextoOpen, setContextoOpen] = useState(false);

  const handleHome = () => {
    (window as any).__atlas_flyToOrder2D?.();
  };

  const baseBtnClass = (active: boolean) =>
    `flex-1 px-1 py-1.5 text-[10px] font-semibold rounded transition-colors ${
      active
        ? 'bg-terracotta text-limestone shadow-sm'
        : 'text-clay hover:text-limestone hover:bg-basalt'
    }`;

  return (
    <div className="bg-schist rounded-lg shadow-lg shadow-black/20 p-2 flex flex-col gap-1 w-[170px]">
      {/* Top actions */}
      <div className="flex gap-1">
        <button onClick={handleHome}
          className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-clay hover:text-terracotta hover:bg-basalt transition-colors text-xs font-medium"
          title="Centrar no pedido">
          <Home size={14} />
          <span>Centrar</span>
        </button>
        <button onClick={() => setShowPolygon(!showPolygon)}
          className={`flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md transition-colors text-xs font-medium ${
            showPolygon ? 'text-terracotta bg-basalt' : 'text-clay hover:text-limestone hover:bg-basalt'
          }`}
          title={showPolygon ? 'Ocultar' : 'Mostrar'}>
          {showPolygon ? <Eye size={14} /> : <EyeOff size={14} />}
          <span>Limite</span>
        </button>
      </div>

      {/* Base layer segmented control */}
      <div className="bg-basalt rounded-md p-0.5 flex gap-0.5">
        <button onClick={() => setBaseLayer('osm')} className={baseBtnClass(baseLayer === 'osm')} title="Mapa OpenStreetMap">
          <Map size={12} className="inline mr-0.5 -mt-px" />Mapa
        </button>
        <button onClick={() => setBaseLayer('satellite')} className={baseBtnClass(baseLayer === 'satellite')} title="Imagem de Satelite">
          <Satellite size={12} className="inline mr-0.5 -mt-px" />Satelite
        </button>
        <button onClick={() => setBaseLayer('terrain')} className={baseBtnClass(baseLayer === 'terrain')} title="Mapa Topografico">
          <Mountain size={12} className="inline mr-0.5 -mt-px" />Terreno
        </button>
      </div>

      {/* Terreno Base group — order-scoped: gate the header + count on layer
          availability, same pattern as Morfologia. The divider is folded inside
          the gate so it never dangles when an order has no terreno-base layers. */}
      {(rasterAvailable.mdt || rasterAvailable.hillshade || rasterAvailable.hypso || contoursTileUrl) && (
        <>
          <div className="border-t border-white/10 my-0.5" />
          <button onClick={() => setTerrenoOpen(!terrenoOpen)}
            className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
            {terrenoOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>Terreno Base</span>
            <span className="text-[9px] text-clay ml-auto">{(rasterAvailable.mdt ? 1 : 0) + (rasterAvailable.hillshade ? 1 : 0) + (rasterAvailable.hypso ? 1 : 0) + (contoursTileUrl ? 1 : 0)}</span>
          </button>
          {terrenoOpen && (
            <div className="flex flex-col gap-0.5 pl-1">
              {rasterAvailable.mdt && (
                <LayerToggle visible={mdtVisible} onToggle={() => toggleRaster('mdt', mdtVisible, setMdtVisible, mdtTileUrl)} loading={layerLoading.mdt}
                  icon={Map} label="MDT" color="text-indigo-600 bg-indigo-50"
                  opacity={mdtOpacity} onOpacity={setMdtOpacity} accentClass="accent-indigo-600" />
              )}
              {rasterAvailable.hillshade && (
                <LayerToggle visible={hillshadeVisible} onToggle={() => toggleRaster('hillshade', hillshadeVisible, setHillshadeVisible, hillshadeTileUrl)} loading={layerLoading.hillshade}
                  icon={Sun} label="Sombreamento" color="text-slate-700 bg-slate-100"
                  opacity={hillshadeOpacity} onOpacity={setHillshadeOpacity} accentClass="accent-slate-600" />
              )}
              {rasterAvailable.hypso && (
                <LayerToggle visible={hillshadeHypsoVisible} onToggle={() => toggleRaster('hypso', hillshadeHypsoVisible, setHillshadeHypsoVisible, hillshadeHypsoTileUrl)} loading={layerLoading.hypso}
                  icon={Palette} label="Hipsometria" color="text-lime-700 bg-lime-50"
                  opacity={hillshadeHypsoOpacity} onOpacity={setHillshadeHypsoOpacity} accentClass="accent-lime-600" />
              )}
              {contoursTileUrl && (
                <LayerToggle visible={contoursVisible} onToggle={() => setContoursVisible(!contoursVisible)}
                  icon={TrendingUp} label="Altimetria" color="text-rose-700 bg-rose-50"
                  opacity={contoursOpacity} onOpacity={setContoursOpacity} accentClass="accent-rose-600" />
              )}
            </div>
          )}
        </>
      )}

      {/* Morfologia group */}
      {(rasterAvailable.slope || rasterAvailable.aspect || rasterAvailable.geomorphons || rasterAvailable.wind) && (
        <>
          <div className="border-t border-white/10 my-0.5" />
          <button onClick={() => setMorfologiaOpen(!morfologiaOpen)}
            className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
            {morfologiaOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>Morfologia</span>
            <span className="text-[9px] text-clay ml-auto">{(rasterAvailable.slope ? 1 : 0) + (rasterAvailable.aspect ? 1 : 0) + (rasterAvailable.geomorphons ? 1 : 0) + (rasterAvailable.wind ? 1 : 0)}</span>
          </button>
          {morfologiaOpen && (
            <div className="flex flex-col gap-0.5 pl-1">
              {rasterAvailable.slope && (
                <LayerToggle visible={slopeVisible} onToggle={() => toggleRaster('slope', slopeVisible, setSlopeVisible, slopeTileUrl)} loading={layerLoading.slope}
                  icon={Triangle} label="Declives" color="text-green-700 bg-green-50"
                  opacity={slopeOpacity} onOpacity={setSlopeOpacity} accentClass="accent-green-600" />
              )}
              {rasterAvailable.aspect && (
                <LayerToggle visible={aspectVisible} onToggle={() => toggleRaster('aspect', aspectVisible, setAspectVisible, aspectTileUrl)} loading={layerLoading.aspect}
                  icon={Compass} label="Exposição Solar" color="text-amber-700 bg-amber-50"
                  opacity={aspectOpacity} onOpacity={setAspectOpacity} accentClass="accent-amber-600" />
              )}
              {rasterAvailable.geomorphons && (
                <LayerToggle visible={geomorphonsVisible} onToggle={() => toggleRaster('geomorphons', geomorphonsVisible, setGeomorphonsVisible, geomorphonsTileUrl)} loading={layerLoading.geomorphons}
                  icon={Shapes} label="Geomorphons" color="text-orange-700 bg-orange-50"
                  opacity={geomorphonsOpacity} onOpacity={setGeomorphonsOpacity} accentClass="accent-orange-600" />
              )}
              {rasterAvailable.wind && (
                <LayerToggle visible={windExpositionVisible} onToggle={() => toggleRaster('wind', windExpositionVisible, setWindExpositionVisible, windExpositionTileUrl)} loading={layerLoading.wind}
                  icon={Wind} label="Exp. ao Vento" color="text-cyan-700 bg-cyan-50"
                  opacity={windExpositionOpacity} onOpacity={setWindExpositionOpacity} accentClass="accent-cyan-600" />
              )}
            </div>
          )}
        </>
      )}

      {/* Erosão group */}
      {rasterAvailable.erosion && (
        <>
          <div className="border-t border-white/10 my-0.5" />
          <button onClick={() => setErosaoOpen(!erosaoOpen)}
            className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
            {erosaoOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>Erosão</span>
            <span className="text-[9px] text-clay ml-auto">{rasterAvailable.erosion ? 1 : 0}</span>
          </button>
          {erosaoOpen && (
            <div className="flex flex-col gap-0.5 pl-1">
              <LayerToggle visible={erosionRiskVisible} onToggle={() => toggleRaster('erosion', erosionRiskVisible, setErosionRiskVisible, erosionRiskTileUrl)} loading={layerLoading.erosion}
                icon={AlertTriangle} label="Risco de Erosão" color="text-amber-700 bg-amber-50"
                opacity={erosionRiskOpacity} onOpacity={setErosionRiskOpacity} accentClass="accent-amber-600" />
            </div>
          )}
        </>
      )}

      {/* Landforms & Keyline group */}
      {(keypointsTileUrl || primaryValleysTileUrl || primaryRidgesTileUrl) && (
        <>
          <div className="border-t border-white/10 my-0.5" />
          <button onClick={() => setLandformsOpen(!landformsOpen)}
            className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
            {landformsOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>Landforms & Keyline</span>
            <span className="text-[9px] text-clay ml-auto">{(keypointsTileUrl ? 1 : 0) + (primaryValleysTileUrl ? 1 : 0) + (primaryRidgesTileUrl ? 1 : 0)}</span>
          </button>
          {landformsOpen && (
            <div className="flex flex-col gap-0.5 pl-1">
              {keypointsTileUrl && (
                <LayerToggle visible={keypointsVisible} onToggle={() => setKeypointsVisible(!keypointsVisible)}
                  icon={Target} label="Keypoints" color="text-amber-600 bg-amber-50"
                  opacity={keypointsOpacity} onOpacity={setKeypointsOpacity} accentClass="accent-amber-500" />
              )}
              {primaryValleysTileUrl && (
                <LayerToggle visible={primaryValleysVisible} onToggle={() => setPrimaryValleysVisible(!primaryValleysVisible)}
                  icon={Droplets} label="Vales Primários" color="text-slate-600 bg-slate-100"
                  opacity={primaryValleysOpacity} onOpacity={setPrimaryValleysOpacity} accentClass="accent-slate-500" />
              )}
              {primaryRidgesTileUrl && (
                <LayerToggle visible={primaryRidgesVisible} onToggle={() => setPrimaryRidgesVisible(!primaryRidgesVisible)}
                  icon={Mountain} label="Cumeadas" color="text-red-700 bg-red-50"
                  opacity={primaryRidgesOpacity} onOpacity={setPrimaryRidgesOpacity} accentClass="accent-red-600" />
              )}
            </div>
          )}
        </>
      )}

      {/* Hidrologia group */}
      {(streamsTileUrl || ridgesTileUrl || rasterAvailable.flow || basinsTileUrl || floodZonesTileUrl) && (
        <>
          <div className="border-t border-white/10 my-0.5" />
          <button onClick={() => setHidrologiaOpen(!hidrologiaOpen)}
            className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
            {hidrologiaOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            <span>Hidrologia</span>
            <span className="text-[9px] text-clay ml-auto">{(streamsTileUrl ? 1 : 0) + (ridgesTileUrl ? 1 : 0) + (rasterAvailable.flow ? 1 : 0) + (basinsTileUrl ? 1 : 0) + (floodZonesTileUrl ? 1 : 0)}</span>
          </button>
          {hidrologiaOpen && (
            <div className="flex flex-col gap-0.5 pl-1">
              {streamsTileUrl && (
                <LayerToggle visible={streamsVisible} onToggle={() => setStreamsVisible(!streamsVisible)}
                  icon={Droplets} label="Linhas de Água" color="text-blue-700 bg-blue-50"
                  opacity={streamsOpacity} onOpacity={setStreamsOpacity} accentClass="accent-blue-600" />
              )}
              {ridgesTileUrl && (
                <LayerToggle visible={ridgesVisible} onToggle={() => setRidgesVisible(!ridgesVisible)}
                  icon={GitBranch} label="Festos" color="text-red-800 bg-red-50"
                  opacity={ridgesOpacity} onOpacity={setRidgesOpacity} accentClass="accent-red-700" />
              )}
              {rasterAvailable.flow && (
                <LayerToggle visible={flowAccVisible} onToggle={() => toggleRaster('flow', flowAccVisible, setFlowAccVisible, flowAccTileUrl)} loading={layerLoading.flow}
                  icon={Waves} label="Acumulação" color="text-teal-700 bg-teal-50"
                  opacity={flowAccOpacity} onOpacity={setFlowAccOpacity} accentClass="accent-teal-600" />
              )}
              {basinsTileUrl && (
                <LayerToggle visible={basinsVisible} onToggle={() => setBasinsVisible(!basinsVisible)}
                  icon={Hexagon} label="Bacias" color="text-purple-700 bg-purple-50"
                  opacity={basinsOpacity} onOpacity={setBasinsOpacity} accentClass="accent-purple-600" />
              )}
              {floodZonesTileUrl && (
                <LayerToggle visible={floodZonesVisible} onToggle={() => setFloodZonesVisible(!floodZonesVisible)}
                  icon={AlertTriangle} label="A. Inundáveis" color="text-red-700 bg-red-50"
                  opacity={floodZonesOpacity} onOpacity={setFloodZonesOpacity} accentClass="accent-red-600" />
              )}
            </div>
          )}
        </>
      )}

      {/* Cadastro group — global context layers, shown only when an order is
          selected (empty toolbar otherwise). */}
      {orderSelected && (<>
      <div className="border-t border-white/10 my-0.5" />
      <button onClick={() => setCadastroOpen(!cadastroOpen)}
        className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
        {cadastroOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Cadastro</span>
        <span className="text-[9px] text-clay ml-auto">4</span>
      </button>
      {cadastroOpen && (
        <div className="flex flex-col gap-0.5 pl-1">
          <LayerToggle visible={cadastroDgtVisible} onToggle={() => setCadastroDgtVisible(!cadastroDgtVisible)}
            icon={Landmark} label="Cadastro DGT" color="text-zinc-800 bg-zinc-100"
            opacity={cadastroDgtOpacity} onOpacity={setCadastroDgtOpacity} accentClass="accent-zinc-700" />
          <LayerToggle visible={bupiRggVisible} onToggle={() => setBupiRggVisible(!bupiRggVisible)}
            icon={Landmark} label="BUPi (RGG)" color="text-amber-900 bg-amber-50"
            opacity={bupiRggOpacity} onOpacity={setBupiRggOpacity} accentClass="accent-amber-800" />
          <LayerToggle visible={freguesiasVisible} onToggle={() => setFreguesiasVisible(!freguesiasVisible)}
            icon={MapPin} label="Freguesias" color="text-violet-700 bg-violet-50"
            opacity={freguesiasOpacity} onOpacity={setFreguesiasOpacity} accentClass="accent-violet-600" />
          <LayerToggle visible={concelhosVisible} onToggle={() => setConcelhosVisible(!concelhosVisible)}
            icon={MapPin} label="Concelhos" color="text-violet-800 bg-violet-50"
            opacity={concelhosOpacity} onOpacity={setConcelhosOpacity} accentClass="accent-violet-700" />
        </div>
      )}

      {/* Restricoes Legais group — REN + RAN. Falls back to global tables
          when an order has no restricoes-legais item; clipped per-order when
          available. */}
      <div className="border-t border-white/10 my-0.5" />
      <button onClick={() => setRestricoesOpen(!restricoesOpen)}
        className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
        {restricoesOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Restrições Legais</span>
        <span className="text-[9px] text-clay ml-auto">3</span>
      </button>
      {restricoesOpen && (
        <div className="flex flex-col gap-0.5 pl-1">
          <LayerToggle visible={renVisible} onToggle={() => setRenVisible(!renVisible)}
            icon={Shield} label="REN" color="text-emerald-800 bg-emerald-50"
            opacity={renOpacity} onOpacity={setRenOpacity} accentClass="accent-emerald-700" />
          <LayerToggle visible={renExclusoesVisible} onToggle={() => setRenExclusoesVisible(!renExclusoesVisible)}
            icon={ScrollText} label="REN (Exclusões)" color="text-emerald-700 bg-emerald-50"
            opacity={renExclusoesOpacity} onOpacity={setRenExclusoesOpacity} accentClass="accent-emerald-600" />
          <LayerToggle visible={ranVisible} onToggle={() => setRanVisible(!ranVisible)}
            icon={Wheat} label="RAN" color="text-yellow-800 bg-yellow-50"
            opacity={ranOpacity} onOpacity={setRanOpacity} accentClass="accent-yellow-600" />
        </div>
      )}

      {/* Contexto (OSM) group — global context layers, gated on orderSelected */}
      <div className="border-t border-white/10 my-0.5" />
      <button onClick={() => setContextoOpen(!contextoOpen)}
        className="flex items-center gap-1 px-1 py-1 text-[11px] font-semibold text-limestone hover:text-terracotta transition-colors">
        {contextoOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <span>Contexto (OSM)</span>
        <span className="text-[9px] text-clay ml-auto">5</span>
      </button>
      {contextoOpen && (
        <div className="flex flex-col gap-0.5 pl-1">
          <LayerToggle visible={roadsVisible} onToggle={() => setRoadsVisible(!roadsVisible)}
            icon={Route} label="Estradas" color="text-gray-700 bg-gray-100"
            opacity={roadsOpacity} onOpacity={setRoadsOpacity} accentClass="accent-gray-600" />
          <LayerToggle visible={buildingsVisible} onToggle={() => setBuildingsVisible(!buildingsVisible)}
            icon={Building2} label="Edifícios" color="text-gray-600 bg-gray-100"
            opacity={buildingsOpacity} onOpacity={setBuildingsOpacity} accentClass="accent-gray-500" />
          <LayerToggle visible={landuseVisible} onToggle={() => setLanduseVisible(!landuseVisible)}
            icon={Trees} label="Uso do Solo" color="text-green-700 bg-green-50"
            opacity={landuseOpacity} onOpacity={setLanduseOpacity} accentClass="accent-green-600" />
          <LayerToggle visible={waterwaysVisible} onToggle={() => setWaterwaysVisible(!waterwaysVisible)}
            icon={Waves} label="Cursos de Água" color="text-blue-500 bg-blue-50"
            opacity={waterwaysOpacity} onOpacity={setWaterwaysOpacity} accentClass="accent-blue-500" />
          <LayerToggle visible={adminBoundsVisible} onToggle={() => setAdminBoundsVisible(!adminBoundsVisible)}
            icon={MapPin} label="Limites Admin." color="text-gray-500 bg-gray-50"
            opacity={adminBoundsOpacity} onOpacity={setAdminBoundsOpacity} accentClass="accent-gray-500" />
        </div>
      )}
      </>)}
    </div>
  );
}
